import { i18n } from '@/plugins/i18n';
import logoPfmd from '../../assets/img/logo-pfmd.png';
import type { Offer, Option, Mover, MoverTag } from '@/types/Offers';
import type { Project } from '@/types/Projects';

const FIRST_STATUS = [
  'REP_RESENT',  // Offer resent to customer
  'REP_NONE',    // No response yet
  'REP_SENT',    // Offer sent to customer
];

// Offer status constants for different stages of the moving process
const INITIAL_STATUS = [
  'REP_RESENT',  // Offer resent to customer
  'REP_NONE',    // No response yet
  'REP_SENT',    // Offer sent to customer
  'REP_FAILURE',   // Offer validation failed
];

const PREBOOKED_STATUS = [
  'REP_PREBOOK_WAIT', // Waiting for pre-booking confirmation
  'REP_PREBOOKED',    // Pre-booking completed
];

const VALIDATION_STATUS = [
  'REP_PREBOOKED', // Pre-booking completed
];

const FINISHED_STATUS = [
  'REP_SUCCESS',    // Move completed successfully
  'REP_SUPERVISION', // Under supervision
  'REP_OPTION',     // Option selected
  'REP_MER_DGR',    // Special status
];

const FAILURE_STATUS = [
  'REP_FAILURE', // Offer failed
];

// Valid statuses for commissionaire customers
const VALID_STATUS = [
  'REP_VALID_FOR_PCD',
  ...FINISHED_STATUS,
];

// Valid statuses for individual customers
const VALID_STATUS_INDIVIDUAL = [
  'REP_PREBOOKED',
  'REP_VALID_FOR_PCD',
  ...FINISHED_STATUS,
];

// Status weighting for offer progression (lower index = earlier stage)
const weighting = [
  INITIAL_STATUS,
  ['REP_PREBOOK_WAIT'],
  VALIDATION_STATUS,
  ['REP_VALID_FOR_PCD'],
  FINISHED_STATUS,
];

export default {
  FINISHED_STATUS,
  INITIAL_STATUS,
  PREBOOKED_STATUS,
  VALIDATION_STATUS,
  VALID_STATUS,
  VALID_STATUS_INDIVIDUAL,
  FAILURE_STATUS,
  /**
   * Get the most advanced offers based on status progression
   * @param {Array} offers - Array of offer objects
   * @returns {Array} Array of most advanced offers
   */
  getMostAvanced(offers: Offer[]): (Offer | {status: string})[] {
    if (!offers.length) {
      return [];
    }
    return offers.reduce((acc, offer: Offer) => {
      if (this.getWeighting(offer) < this.getWeighting(acc[0])) {
        return acc;
      } if (this.getWeighting(offer) > this.getWeighting(acc[0])) {
        return [offer];
      }
      return [...acc, offer];
    }, [{ status: '' }]);
  },
  getNotInitialPrebookedOffers(offers: Offer[]) {
    return offers.filter(({ status }) => ![...INITIAL_STATUS, ...PREBOOKED_STATUS]
      .includes(status));
  },
  getOptionsSelected({ options }: { options: Option[] }) {
    return options.filter((option) => option.is_chosen);
  },
  getPrebookedOffers(offers: Offer[]) {
    return offers.filter(({ status }) => PREBOOKED_STATUS.includes(status));
  },
  getWaitingPrebookedOffers(offers: Offer[]) {
    return offers.filter((offer) => this.isWaitingforQuotation(offer));
  },
  getOptionsPrice(offer: Offer, exclTax: boolean) {
    const price = exclTax ? 'price_ht' : 'price';
    return Math.round(((offer.options ? offer.options.reduce(
      (acc, elem) => (acc + (elem.is_chosen ? elem[price] : 0)),
      0,
    ) : 0) * 100)) / 100;
  },
  getPriceBeforeDiscountFromRate(price: number, discountRate: number) {
    if (!discountRate || discountRate >= 100) {
      return price;
    }

    return Math.round((price / (1 - (discountRate / 100))) * 100) / 100;
  },
  getOptionPriceBeforeDiscount(option: Option, offer: Offer, exclTax: boolean) {
    const price = exclTax ? option.price_ht : option.price;
    return this.getPriceBeforeDiscountFromRate(price, this.getPartnerDiscountRate(offer));
  },
  getOptionsPriceBeforeDiscount(offer: Offer, exclTax: boolean) {
    const optionsPrice = this.getOptionsPrice(offer, exclTax);
    return this.getPriceBeforeDiscountFromRate(optionsPrice, this.getPartnerDiscountRate(offer));
  },
  getPriceBeforeDiscount(offer: Offer, exclTax: boolean) {
    const price = exclTax ? 'price_before_discount_ht' : 'price_before_discount';
    const fallbackPrice = exclTax ? offer.price_ht : offer.price;
    const total = (offer?.[price] ?? fallbackPrice) + this.getOptionsPriceBeforeDiscount(offer, exclTax);
    return Math.round(total * 100) / 100;
  },
  getPartnerDiscountAmount(offer: Offer, exclTax: boolean) {
    const price = exclTax ? 'partner_discount_amount_ht' : 'partner_discount_amount';
    const optionsDiscountAmount = this.getOptionsPriceBeforeDiscount(offer, exclTax)
      - this.getOptionsPrice(offer, exclTax);

    return Math.round((((offer?.[price] || 0) + optionsDiscountAmount) * 100)) / 100;
  },
  getPartnerDiscountRate(offer: Offer) {
    return Math.round(((offer?.partner_discount_rate || 0) * 100)) / 100;
  },
  hasPartnerDiscount(offer: Offer) {
    return Boolean(offer?.has_partner_discount && this.getPartnerDiscountAmount(offer, false) > 0);
  },
  /**
   * Calculate total price for an offer including selected options
   * @param {Object} offer - Offer object with price and options
   * @param {boolean} exclTax - Whether to use price excluding tax
   * @returns {number} Total price rounded to 2 decimal places
   */
  getPrice(offer: Offer, exclTax: boolean) {
    const price = exclTax ? 'price_ht' : 'price';
    const total = offer[price] + this.getOptionsPrice(offer, exclTax);
    return Math.round(total * 100) / 100;
  },
  getRepaymentCeiling({ plafond }: { plafond: number | null }) {
    if (plafond) {
      return Math.round(plafond * 100) / 100;
    }
    return null;
  },
  getValidOffer(offers: Offer[], isIndividual: boolean) {
    return offers.find((offer) => this.isValidOffer(offer, isIndividual));
  },
  getWeighting({ status }: { status: string }) {
    return weighting.findIndex((weight) => weight.includes(status));
  },
  getFinishedOffer(offers: Offer[]) {
    return offers.find(({ status }) => FINISHED_STATUS.includes(status));
  },
  isFinishedState(offers: Offer[]) {
    return !!this.getFinishedOffer(offers);
  },
  isFinished(offers: Offer[]) {
    return !!offers.find(
      (offer) => (!offer.signing_procedure || this.isSigned(offer))
        && this.isFinishedStatus(offer),
    );
  },
  isFinishedStatus({ status }: { status: string }) {
    return FINISHED_STATUS.includes(status);
  },
  isFailureOrInitialState(offers: Offer[], nbPrebookedOffer: number) {
    return offers.filter(
      ({ status }) => ![...FAILURE_STATUS, ...INITIAL_STATUS].includes(status),
    ).length < nbPrebookedOffer;
  },
  isInitialState(offers: Offer[], nbPrebookedOffer: number = 0) {
    return offers.filter(
      ({ status }) => !INITIAL_STATUS.includes(status),
    ).length < nbPrebookedOffer;
  },
  isFirstState(offers: Offer[]) {
    return offers.filter(
      ({ status }) => !FIRST_STATUS.includes(status),
    ).length === 0;
  },
  isInitialStatus({ status }: { status: string }) {
    return INITIAL_STATUS.includes(status);
  },
  isFailureStatus({ status }: { status: string }) {
    return FAILURE_STATUS.includes(status);
  },
  isOpened(offer: Offer, offers: Offer[]) {
    return this.isInitialState(offers)
      || this.getMostAvanced(offers).find(({ status }) => offer.status === status);
  },
  isSigned(offer: Offer) {
    return offer?.signing_procedure?.is_finished;
  },
  isValidationStatus({ status }: { status: string }) {
    return VALIDATION_STATUS.includes(status);
  },
  isPrebooked({ status }: { status: string }) {
    return status === 'REP_PREBOOKED';
  },
  isValidForPcd({ status }: { status: string }) {
    return status === 'REP_VALID_FOR_PCD';
  },
  isWaitingforQuotation({ status }: { status: string }) {
    return status === 'REP_PREBOOK_WAIT';
  },
  isValidOffer(offer: Offer, isIndividual: boolean) {
    if (isIndividual) {
      return VALID_STATUS_INDIVIDUAL.includes(offer.status);
    }
    return VALID_STATUS.includes(offer.status);
  },
  needsFirstSignature(offers: Offer[]) {
    const finishedOffer = this.getFinishedOffer(offers);
    if (finishedOffer && finishedOffer.status === 'REP_OPTION') {
      return !this.isSigned(finishedOffer);
    }
    return false;
  },
  needsNewSignature(offers: Offer[]) {
    const finishedOffer = this.getFinishedOffer(offers);
    if (finishedOffer && finishedOffer.status !== 'REP_OPTION') {
      return !this.isSigned(finishedOffer);
    }
    return false;
  },
  showMoverName(projet: Project, offer: Offer) {
    return projet.params?.dem_name || !this.isInitialStatus(offer);
  },
  formatOffer(offer: Offer, project: Project, index?: number) {
    const showMoverName = offer.mover && this.showMoverName(project, offer);
    return {
      ...offer,
      mover: {
        ...offer.mover,
        customLogo: showMoverName ? offer.mover.logo_url
          : logoPfmd,
        customName: showMoverName ? offer.mover.name
          : `${i18n.global.t('base.mover')} ${(index !== null && index !== undefined) ? index + 1 : ''}`,
      },
    };
  },
  formatOffers(offers: Offer[], project: Project) {
    return offers.map((offer, index) => this.formatOffer(offer, project, index));
  },
  formatMoverTags(mover: Mover, tags: MoverTag[]) {
    if (mover?.mover_tags) {
      // Format the tags because it's a string
      // Exemple: 'bet-mover,servie-client'
      const moverTags = typeof mover.mover_tags === 'string'
        ? mover.mover_tags.split(',')
        : mover.mover_tags;
      // Get the tags from the moverapp list
      return tags.filter((tag) => moverTags.includes(tag.tag_id));
    }
    return [];
  },
};
